import PropTypes from 'prop-types';
import { Button, ButtonGroup } from 'react-bootstrap';
import { FaPrint } from 'react-icons/fa';
import { useState } from 'react';
import { getActiveAccountId } from '../utils/accountSessions';

const CompactPrescriptionView = ({ prescription, onDownload, className = '', style = {} }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingA4, setIsDownloadingA4] = useState(false);

  const handleDownload = async () => {
    if (typeof onDownload === 'function') {
      onDownload();
      return;
    }

    if (!prescription || !prescription.id) {
      console.error('Prescription ID is required for PDF download');
      return;
    }

    setIsDownloading(true);
    
    try {
      const activeAccountId = getActiveAccountId();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        headers['X-Account-Id'] = String(activeAccountId);
      }
      
      // Step 1: Generate and store the PDF
      const generateResponse = await fetch(`${process.env.REACT_APP_API_URL}prescriptions/${prescription.id}/pdf`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!generateResponse.ok) {
        let serverMessage = '';
        try {
          const contentType = generateResponse.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await generateResponse.json();
            serverMessage = data?.error || data?.message || JSON.stringify(data);
          } else {
            serverMessage = await generateResponse.text();
          }
        } catch (_) {
          // Ignore parsing errors
        }
        const msg = `Failed to generate PDF: ${generateResponse.status}${serverMessage ? ` - ${serverMessage}` : ''}`;
        throw new Error(msg);
      }

      const generateData = await generateResponse.json();
      const pdfId = generateData.pdf_id;

      if (!pdfId) {
        throw new Error('PDF ID not received from server');
      }

      // Step 2: Get signed URL for the PDF
      const signedUrlHeaders = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        signedUrlHeaders['X-Account-Id'] = String(activeAccountId);
      }

      const signedUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}prescription-pdfs/${pdfId}/signed-url`, {
        method: 'GET',
        headers: signedUrlHeaders,
        credentials: 'include',
      });

      if (!signedUrlResponse.ok) {
        throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
      }

      const signedUrlData = await signedUrlResponse.json();
      const pdfUrl = signedUrlData.url;

      // Open PDF using anchor tag click (works in iOS WebView)
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading prescription PDF:', error);
      alert(`Failed to download prescription PDF. Error: ${error?.message || error}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleA4Download = async () => {
    if (!prescription || !prescription.id) {
      console.error('Prescription ID is required for A4 PDF download');
      return;
    }

    setIsDownloadingA4(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const activeAccountId = getActiveAccountId();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        headers['X-Account-Id'] = String(activeAccountId);
      }
      
      // Step 1: Generate and store the A4 PDF
      const generateResponse = await fetch(`${API_URL}prescriptions/${prescription.id}/pdf-a4`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!generateResponse.ok) {
        let serverMessage = '';
        try {
          const contentType = generateResponse.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await generateResponse.json();
            serverMessage = data?.error || data?.message || JSON.stringify(data);
          } else {
            serverMessage = await generateResponse.text();
          }
        } catch (_) {
          // Ignore parsing errors
        }
        const msg = `Failed to generate A4 PDF: ${generateResponse.status}${serverMessage ? ` - ${serverMessage}` : ''}`;
        throw new Error(msg);
      }

      const generateData = await generateResponse.json();
      const pdfId = generateData.pdf_id;

      if (!pdfId) {
        throw new Error('PDF ID not received from server');
      }

      // Step 2: Get signed URL for the PDF
      const signedUrlHeaders = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        signedUrlHeaders['X-Account-Id'] = String(activeAccountId);
      }

      const signedUrlResponse = await fetch(`${API_URL}prescription-pdfs/${pdfId}/signed-url`, {
        method: 'GET',
        headers: signedUrlHeaders,
        credentials: 'include',
      });

      if (!signedUrlResponse.ok) {
        throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
      }

      const signedUrlData = await signedUrlResponse.json();
      const pdfUrl = signedUrlData.url;

      // Open PDF using anchor tag click (works in iOS WebView)
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading A4 prescription PDF:', error);
      alert(`Failed to download A4 prescription PDF. Error: ${error?.message || error}`);
    } finally {
      setIsDownloadingA4(false);
    }
  };

  if (!prescription) return null;
  
  return (
    <ButtonGroup size="sm" className={`ios-app rx-actions ${className}`.trim()}>
      <Button
        variant="outline-primary"
        onClick={handleDownload}
        disabled={isDownloading || isDownloadingA4}
        style={{
          ...style
        }}
      >
        <FaPrint className="me-1" /> 
        <span className="btn-label">{isDownloading ? 'Loading...' : 'Compact'}</span>
      </Button>
      <Button
        variant="outline-primary"
        onClick={handleA4Download}
        disabled={isDownloading || isDownloadingA4}
        style={{
          ...style
        }}
      >
        <FaPrint className="me-1" /> 
        <span className="btn-label">{isDownloadingA4 ? 'Loading...' : 'Full'}</span>
      </Button>
    </ButtonGroup>
  );
};

CompactPrescriptionView.propTypes = {
  prescription: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    treatments: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          value: PropTypes.string,
        }),
      ])
    ),
    diagnosis: PropTypes.string,

  }),
  patientName: PropTypes.string,
  onDownload: PropTypes.func,
};

export default CompactPrescriptionView;
